<template>
  <span v-if="!codes || !codes.length" class="investigation-code-chips__none">Geen</span>
  <div v-else class="investigation-code-chips">
    <span
      v-for="code in codes"
      :key="code"
      class="ds-chip ds-chip--code"
      :class="{
        'investigation-code-chips__chip--zero': isZero(code),
        'investigation-code-chips__chip--described': describe(code),
      }"
      @mouseenter="open(code, $event)"
      @mouseleave="close"
      >{{ code }}</span
    >
    <span
      v-if="tip"
      ref="tip"
      class="investigation-code-chips__tooltip"
      :class="{ 'investigation-code-chips__tooltip--below': tip.below }"
      role="tooltip"
      :style="tipStyle"
      >{{ tip.text }}</span
    >
  </div>
</template>

<script>
// A list of option / feature codes. A code the standard-options call described
// carries that description as a tooltip, so a bare PR code can be read without
// leaving the page.

// Distance between the chip and the tooltip, the margin the tooltip is kept
// away from the viewport edges, and how far the arrow stays off its corners.
const GAP = 8
const EDGE = 8
const ARROW_INSET = 14

export default {
  name: 'InvestigationCodeChips',
  props: {
    codes: {
      type: Array,
      default: () => [],
    },
    // Codes to mark as the ones the finding is about (a zero price).
    zero: {
      type: Array,
      default: () => [],
    },
    // code -> description, built from the GET_STANDARD_OPTIONS response.
    descriptions: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return { tip: null }
  },
  computed: {
    // Hidden until `place()` has measured it: the first paint is at 0,0.
    tipStyle() {
      if (!this.tip) return null
      return {
        left: `${this.tip.left}px`,
        top: `${this.tip.top}px`,
        '--tip-arrow': `${this.tip.arrow}px`,
        opacity: this.tip.placed ? 1 : 0,
      }
    },
  },
  beforeUnmount() {
    this.close()
  },
  methods: {
    isZero(code) {
      return this.zero.includes(code)
    },
    describe(code) {
      return this.descriptions[code] || null
    },
    open(code, event) {
      const text = this.describe(code)
      if (!text) return

      const { left, right, top, bottom } = event.currentTarget.getBoundingClientRect()
      this.tip = {
        text,
        anchor: { left, right, top, bottom },
        left: 0,
        top: 0,
        arrow: 0,
        below: false,
        placed: false,
      }

      // The anchor is measured once, so anything that moves it closes the
      // tooltip rather than leaving it behind.
      window.addEventListener('scroll', this.close, true)
      window.addEventListener('resize', this.close)
      this.$nextTick(this.place)
    },
    // The tooltip is only as wide as its text, so where it fits can only be
    // decided once it has been rendered. It sits above the chip unless that
    // would push it off the top of the window, and is kept inside the window
    // horizontally — a chip near either edge would otherwise run off it. The
    // arrow then stays behind on the chip the tooltip was nudged away from.
    place() {
      const element = this.$refs.tip
      if (!element || !this.tip) return

      const { width, height } = element.getBoundingClientRect()
      const { anchor } = this.tip
      const centre = (anchor.left + anchor.right) / 2
      const above = anchor.top - GAP - height
      const left = Math.max(EDGE, Math.min(centre - width / 2, window.innerWidth - width - EDGE))

      this.tip = {
        ...this.tip,
        left,
        top: above < EDGE ? anchor.bottom + GAP : above,
        below: above < EDGE,
        arrow: Math.max(ARROW_INSET, Math.min(centre - left, width - ARROW_INSET)),
        placed: true,
      }
    },
    close() {
      if (!this.tip) return
      this.tip = null
      window.removeEventListener('scroll', this.close, true)
      window.removeEventListener('resize', this.close)
    },
  },
}
</script>

<style lang="scss" scoped>
.investigation-code-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  &__chip--zero {
    background: var(--ds-chip-warning-bg);
    color: var(--ds-chip-warning-text);
  }

  // A described code reads as explainable: dotted underline, tooltip on hover.
  &__chip--described {
    cursor: help;
    text-decoration: underline dotted;
    text-decoration-color: var(--ds-color-text-subtle);
    text-underline-offset: 3px;
  }

  // Positioned `fixed` from script rather than `absolute` off the chip: the
  // cards these chips sit in clip their overflow, so a tooltip in the flow gets
  // cut off at the card edge. Fixed takes it out of every ancestor's clip and
  // above the rest of the page — same approach as .ds-popover.
  &__tooltip {
    position: fixed;
    z-index: 1000;
    min-width: 120px;
    max-width: 280px;
    width: max-content;
    padding: 8px 11px;
    border-radius: var(--ds-radius-md);
    background: var(--ds-color-brand);
    color: var(--ds-color-surface);
    font-family: var(--ds-font-family);
    font-size: 12.5px;
    font-weight: 500;
    line-height: 1.4;
    letter-spacing: 0;
    text-align: left;
    white-space: normal;
    box-shadow: var(--ds-shadow-md);
    pointer-events: none;
    transition: opacity 0.12s ease;

    // Arrow, pointing back at the chip. `--tip-arrow` is its distance from the
    // tooltip's left edge, so it keeps pointing at the chip when the tooltip
    // has been nudged sideways to stay in the window.
    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: var(--tip-arrow, 50%);
      margin-left: -5px;
      border: 5px solid transparent;
      border-top-color: var(--ds-color-brand);
    }

    &--below::after {
      top: auto;
      bottom: 100%;
      border-top-color: transparent;
      border-bottom-color: var(--ds-color-brand);
    }
  }

  &__none {
    color: var(--ds-color-text-subtle);
  }
}
</style>
